import React, { useState, useMemo, useEffect } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  Platform,
  Alert,
  Modal,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ColDef } from 'ag-grid-community';
import { useTheme } from '../../../hooks/useTheme';
import { palette } from '../../../utils/palette';
import Text from '../../../components/common/Text';
import Button from '../../../components/common/Button';
import SearchableSelector from '../../../components/common/SearchableSelector';
import AdminGrid from './AdminGrid';
import {
  useFeatureFlags,
  useCreateFeatureFlag,
  useUpdateFeatureFlag,
  useDeleteFeatureFlag,
  useFeatureFlagDetails,
  useAssignFeatureFlagToUser,
  useRemoveFeatureFlagFromUser,
} from '../../../hooks/useAdminData';
import { useUsers } from '../../../hooks/useAdminData';

interface FeatureFlag {
  id: number;
  name: string;
  description?: string;
  is_enabled_globally: boolean;
  enabled_for_roles?: string[];
  assigned_user_count: number;
  created_at: string;
  updated_at: string;
}

interface FeatureFlagsGridProps {
  searchValue?: string;
  onPageChange?: (page: number) => void;
  currentPage?: number;
  pageSize?: number;
}

const FeatureFlagsGrid: React.FC<FeatureFlagsGridProps> = ({
  searchValue = '',
  onPageChange: _onPageChange,
  currentPage = 1,
  pageSize = 10,
}) => {
  const { colors } = useTheme();
  const [selectedFlag, setSelectedFlag] = useState<FeatureFlag | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [newFlagName, setNewFlagName] = useState('');
  const [newFlagDescription, setNewFlagDescription] = useState('');
  const [newFlagGlobal, setNewFlagGlobal] = useState(false);
  const [newFlagRoles, setNewFlagRoles] = useState<string[]>([]);
  const [showUserAssignmentModal, setShowUserAssignmentModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

  const { data: flagsData, isLoading: _isLoading } = useFeatureFlags({
    page: currentPage,
    limit: pageSize,
  });
  const { data: usersData } = useUsers({ page: 1, limit: 1000 });
  const { data: flagDetails } = useFeatureFlagDetails(selectedFlag?.id || 0, {
    enabled: !!selectedFlag && showDetailsModal,
  });

  const createFlag = useCreateFeatureFlag();
  const updateFlag = useUpdateFeatureFlag();
  const deleteFlag = useDeleteFeatureFlag();
  const assignFlag = useAssignFeatureFlagToUser();
  const removeFlag = useRemoveFeatureFlagFromUser();

  const flags = flagsData?.items || [];
  const users = usersData?.items || [];

  // Reset selected user when modal opens/closes
  useEffect(() => {
    if (!showUserAssignmentModal) {
      setSelectedUserId(null);
    }
  }, [showUserAssignmentModal]);

  const filteredFlags = useMemo(() => {
    if (!searchValue.trim()) return flags;
    const lowerSearch = searchValue.toLowerCase();
    return flags.filter((flag: FeatureFlag) => {
      const searchFields = [flag.name, flag.description, flag.id?.toString()];
      return searchFields.some(
        (field) => field && String(field).toLowerCase().includes(lowerSearch)
      );
    });
  }, [flags, searchValue]);

  const columnDefs: ColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 80, sortable: true },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        sortable: true,
        filter: true,
        cellRenderer: (params: any) => (
          <span style={{ fontWeight: '600' }}>{params.value}</span>
        ),
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 2,
        sortable: true,
        cellRenderer: (params: any) => (
          <span style={{ color: colors.secondary }}>{params.value || '—'}</span>
        ),
      },
      {
        field: 'is_enabled_globally',
        headerName: 'Global',
        width: 100,
        sortable: true,
        cellRenderer: (params: any) => (
          <span
            style={{
              padding: '4px 8px',
              borderRadius: '4px',
              backgroundColor: params.value
                ? `${palette.brightGreen}20`
                : `${palette.darkGrayBlue}20`,
              color: params.value ? palette.brightGreen : palette.darkGrayBlue,
              fontSize: '12px',
              fontWeight: '600',
            }}
          >
            {params.value ? 'Yes' : 'No'}
          </span>
        ),
      },
      {
        field: 'enabled_for_roles',
        headerName: 'Roles',
        width: 150,
        cellRenderer: (params: any) => {
          const roles = params.value || [];
          if (roles.length === 0) return <span>—</span>;
          return <span style={{ fontSize: '12px' }}>{roles.join(', ')}</span>;
        },
      },
      {
        field: 'assigned_user_count',
        headerName: 'Users',
        width: 100,
        sortable: true,
        cellRenderer: (params: any) => <span>{params.value || 0}</span>,
      },
      {
        headerName: 'Actions',
        width: 280,
        cellRenderer: (params: any) => {
          const flag = params.data as FeatureFlag;
          return (
            <div
              style={{
                display: 'flex',
                gap: '4px',
                alignItems: 'center',
                height: '100%',
                flexWrap: 'wrap',
              }}
            >
              <button
                onClick={() => {
                  setSelectedFlag(flag);
                  setShowDetailsModal(true);
                }}
                style={{
                  padding: '4px 8px',
                  backgroundColor: palette.brightBlue,
                  color: palette.white,
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '10px',
                }}
                title="View Details"
              >
                View
              </button>
              <button
                onClick={() => {
                  setSelectedFlag(flag);
                  setNewFlagName(flag.name);
                  setNewFlagDescription(flag.description || '');
                  setNewFlagGlobal(flag.is_enabled_globally);
                  setNewFlagRoles(flag.enabled_for_roles || []);
                  setShowEditModal(true);
                }}
                style={{
                  padding: '4px 8px',
                  backgroundColor: palette.lightOrange,
                  color: palette.white,
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '10px',
                }}
                title="Edit"
              >
                Edit
              </button>
              <button
                onClick={() => {
                  updateFlag.mutate({
                    flagId: flag.id,
                    data: {
                      is_enabled_globally: !flag.is_enabled_globally,
                    },
                  });
                }}
                style={{
                  padding: '4px 8px',
                  backgroundColor: flag.is_enabled_globally
                    ? palette.darkGrayBlue
                    : palette.cyanLimeGreen,
                  color: palette.white,
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '10px',
                }}
                title={
                  flag.is_enabled_globally
                    ? 'Disable Globally'
                    : 'Enable Globally'
                }
              >
                {flag.is_enabled_globally ? 'Disable' : 'Enable'}
              </button>
              <button
                onClick={() => {
                  if (
                    confirm(`Are you sure you want to delete "${flag.name}"?`)
                  ) {
                    deleteFlag.mutate(flag.id);
                  }
                }}
                style={{
                  padding: '4px 8px',
                  backgroundColor: palette.brightMatteRed,
                  color: palette.white,
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '10px',
                }}
                title="Delete"
              >
                Delete
              </button>
            </div>
          );
        },
      },
    ],
    [
      colors,
      deleteFlag,
      updateFlag,
      setSelectedFlag,
      setShowDetailsModal,
      setShowEditModal,
      setNewFlagName,
      setNewFlagDescription,
      setNewFlagGlobal,
      setNewFlagRoles,
    ]
  );

  const handleCreateFlag = () => {
    if (!newFlagName.trim()) {
      Alert.alert('Error', 'Flag name is required');
      return;
    }
    createFlag.mutate(
      {
        name: newFlagName.trim(),
        description: newFlagDescription.trim() || undefined,
        is_enabled_globally: newFlagGlobal,
        enabled_for_roles: newFlagRoles.length > 0 ? newFlagRoles : undefined,
      },
      {
        onSuccess: () => {
          setShowCreateModal(false);
          setNewFlagName('');
          setNewFlagDescription('');
          setNewFlagGlobal(false);
          setNewFlagRoles([]);
        },
      }
    );
  };

  const handleUpdateFlag = () => {
    if (!selectedFlag) return;
    updateFlag.mutate(
      {
        flagId: selectedFlag.id,
        data: {
          description: newFlagDescription.trim() || undefined,
          is_enabled_globally: newFlagGlobal,
          enabled_for_roles: newFlagRoles.length > 0 ? newFlagRoles : [], // Send empty array to clear roles
        },
      },
      {
        onSuccess: () => {
          setShowEditModal(false);
          setSelectedFlag(null);
        },
      }
    );
  };

  const handleAssignUser = () => {
    if (!selectedFlag || !selectedUserId) return;
    // Always enable when assigning a user
    assignFlag.mutate(
      {
        flagId: selectedFlag.id,
        userId: selectedUserId,
        isEnabled: true,
      },
      {
        onSuccess: () => {
          setShowUserAssignmentModal(false);
          setSelectedUserId(null);
        },
      }
    );
  };

  const handleRemoveUser = (userId: number) => {
    if (!selectedFlag) return;

    // Use confirm for web compatibility
    if (Platform.OS === 'web') {
      if (confirm('Are you sure you want to remove this user assignment?')) {
        removeFlag.mutate({
          flagId: selectedFlag.id,
          userId,
        });
      }
    } else {
      Alert.alert(
        'Remove Assignment',
        'Are you sure you want to remove this user assignment?',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Remove',
            style: 'destructive',
            onPress: () => {
              removeFlag.mutate({
                flagId: selectedFlag.id,
                userId,
              });
            },
          },
        ]
      );
    }
  };

  const toggleRole = (role: string) => {
    setNewFlagRoles((prev) =>
      prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
  };

  if (Platform.OS !== 'web') {
    return (
      <View style={styles.mobileContainer}>
        <Text style={[styles.mobileTitle, { color: colors.text }]}>
          Feature Flags
        </Text>
        <Text style={[styles.mobileSubtitle, { color: colors.secondary }]}>
          Feature flag management is available on web only.
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={[styles.title, { color: colors.text }]}>
          Feature Flags
        </Text>
        <Button
          title="Create Flag"
          onPress={() => setShowCreateModal(true)}
          style={styles.createButton}
        />
      </View>

      <AdminGrid
        rowData={filteredFlags}
        columnDefs={columnDefs}
        searchValue={searchValue}
      />

      {/* Create Modal */}
      <Modal
        visible={showCreateModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowCreateModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View
            style={[
              styles.modalContent,
              { backgroundColor: colors.background },
            ]}
          >
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: colors.text }]}>
                Create Feature Flag
              </Text>
              <TouchableOpacity onPress={() => setShowCreateModal(false)}>
                <Ionicons name="close" size={24} color={colors.text} />
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.modalBody}>
              <View style={styles.formGroup}>
                <Text style={[styles.label, { color: colors.text }]}>
                  Name *
                </Text>
                <TextInput
                  style={[
                    styles.input,
                    {
                      backgroundColor: colors.card,
                      color: colors.text,
                      borderColor: colors.border,
                    },
                  ]}
                  value={newFlagName}
                  onChangeText={setNewFlagName}
                  placeholder="e.g., pricing_subscription"
                  placeholderTextColor={colors.secondary}
                />
              </View>
              <View style={styles.formGroup}>
                <Text style={[styles.label, { color: colors.text }]}>
                  Description
                </Text>
                <TextInput
                  style={[
                    styles.textArea,
                    {
                      backgroundColor: colors.card,
                      color: colors.text,
                      borderColor: colors.border,
                    },
                  ]}
                  value={newFlagDescription}
                  onChangeText={setNewFlagDescription}
                  placeholder="Description of what this flag controls"
                  placeholderTextColor={colors.secondary}
                  multiline
                  numberOfLines={3}
                />
              </View>
              <View style={styles.formGroup}>
                <TouchableOpacity
                  style={styles.checkboxRow}
                  onPress={() => setNewFlagGlobal(!newFlagGlobal)}
                >
                  <Ionicons
                    name={newFlagGlobal ? 'checkbox' : 'checkbox-outline'}
                    size={24}
                    color={colors.primary}
                  />
                  <Text style={[styles.checkboxLabel, { color: colors.text }]}>
                    Enable globally for all users
                  </Text>
                </TouchableOpacity>
              </View>
              <View style={styles.formGroup}>
                <Text style={[styles.label, { color: colors.text }]}>
                  Enable for Roles
                </Text>
                {['user', 'moderator', 'admin'].map((role) => (
                  <TouchableOpacity
                    key={role}
                    style={styles.checkboxRow}
                    onPress={() => toggleRole(role)}
                  >
                    <Ionicons
                      name={
                        newFlagRoles.includes(role)
                          ? 'checkbox'
                          : 'checkbox-outline'
                      }
                      size={24}
                      color={colors.primary}
                    />
                    <Text
                      style={[styles.checkboxLabel, { color: colors.text }]}
                    >
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>
            <View style={styles.modalFooter}>
              <Button
                title="Cancel"
                onPress={() => setShowCreateModal(false)}
                variant="outline"
                style={styles.modalButton}
              />
              <Button
                title="Create"
                onPress={handleCreateFlag}
                loading={createFlag.isPending}
                style={styles.modalButton}
              />
            </View>
          </View>
        </View>
      </Modal>

      {/* Edit Modal */}
      <Modal
        visible={showEditModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowEditModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View
            style={[
              styles.modalContent,
              { backgroundColor: colors.background },
            ]}
          >
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: colors.text }]}>
                Edit Feature Flag
              </Text>
              <TouchableOpacity onPress={() => setShowEditModal(false)}>
                <Ionicons name="close" size={24} color={colors.text} />
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.modalBody}>
              <View style={styles.formGroup}>
                <Text style={[styles.label, { color: colors.text }]}>Name</Text>
                <Text
                  style={[
                    styles.readOnlyInput,
                    {
                      backgroundColor: colors.card,
                      color: colors.secondary,
                      borderColor: colors.border,
                    },
                  ]}
                >
                  {selectedFlag?.name}
                </Text>
              </View>
              <View style={styles.formGroup}>
                <Text style={[styles.label, { color: colors.text }]}>
                  Description
                </Text>
                <TextInput
                  style={[
                    styles.textArea,
                    {
                      backgroundColor: colors.card,
                      color: colors.text,
                      borderColor: colors.border,
                    },
                  ]}
                  value={newFlagDescription}
                  onChangeText={setNewFlagDescription}
                  placeholder="Description of what this flag controls"
                  placeholderTextColor={colors.secondary}
                  multiline
                  numberOfLines={3}
                />
              </View>
              <View style={styles.formGroup}>
                <TouchableOpacity
                  style={styles.checkboxRow}
                  onPress={() => setNewFlagGlobal(!newFlagGlobal)}
                >
                  <Ionicons
                    name={newFlagGlobal ? 'checkbox' : 'checkbox-outline'}
                    size={24}
                    color={colors.primary}
                  />
                  <Text style={[styles.checkboxLabel, { color: colors.text }]}>
                    Enable globally for all users
                  </Text>
                </TouchableOpacity>
              </View>
              <View style={styles.formGroup}>
                <Text style={[styles.label, { color: colors.text }]}>
                  Enable for Roles
                </Text>
                {['user', 'moderator', 'admin'].map((role) => (
                  <TouchableOpacity
                    key={role}
                    style={styles.checkboxRow}
                    onPress={() => toggleRole(role)}
                  >
                    <Ionicons
                      name={
                        newFlagRoles.includes(role)
                          ? 'checkbox'
                          : 'checkbox-outline'
                      }
                      size={24}
                      color={colors.primary}
                    />
                    <Text
                      style={[styles.checkboxLabel, { color: colors.text }]}
                    >
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>
            <View style={styles.modalFooter}>
              <Button
                title="Cancel"
                onPress={() => setShowEditModal(false)}
                variant="outline"
                style={styles.modalButton}
              />
              <Button
                title="Update"
                onPress={handleUpdateFlag}
                loading={updateFlag.isPending}
                style={styles.modalButton}
              />
            </View>
          </View>
        </View>
      </Modal>

      {/* Details Modal */}
      <Modal
        visible={showDetailsModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowDetailsModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View
            style={[
              styles.modalContent,
              { backgroundColor: colors.background },
            ]}
          >
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: colors.text }]}>
                Feature Flag Details
              </Text>
              <TouchableOpacity onPress={() => setShowDetailsModal(false)}>
                <Ionicons name="close" size={24} color={colors.text} />
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.modalBody}>
              {flagDetails && (
                <>
                  <View style={styles.detailRow}>
                    <Text
                      style={[styles.detailLabel, { color: colors.secondary }]}
                    >
                      Name:
                    </Text>
                    <Text style={[styles.detailValue, { color: colors.text }]}>
                      {flagDetails.name}
                    </Text>
                  </View>
                  <View style={styles.detailRow}>
                    <Text
                      style={[styles.detailLabel, { color: colors.secondary }]}
                    >
                      Description:
                    </Text>
                    <Text style={[styles.detailValue, { color: colors.text }]}>
                      {flagDetails.description || '—'}
                    </Text>
                  </View>
                  <View style={styles.detailRow}>
                    <Text
                      style={[styles.detailLabel, { color: colors.secondary }]}
                    >
                      Global:
                    </Text>
                    <Text style={[styles.detailValue, { color: colors.text }]}>
                      {flagDetails.is_enabled_globally ? 'Yes' : 'No'}
                    </Text>
                  </View>
                  <View style={styles.detailRow}>
                    <Text
                      style={[styles.detailLabel, { color: colors.secondary }]}
                    >
                      Roles:
                    </Text>
                    <Text style={[styles.detailValue, { color: colors.text }]}>
                      {flagDetails.enabled_for_roles?.join(', ') || '—'}
                    </Text>
                  </View>
                  <View style={styles.detailSection}>
                    <View style={styles.detailSectionHeader}>
                      <Text
                        style={[
                          styles.detailSectionTitle,
                          { color: colors.text },
                        ]}
                      >
                        User Assignments
                      </Text>
                      <Button
                        title="Assign User"
                        onPress={() => setShowUserAssignmentModal(true)}
                        style={styles.smallButton}
                      />
                    </View>
                    {flagDetails.user_assignments &&
                    flagDetails.user_assignments.length > 0 ? (
                      flagDetails.user_assignments.map((assignment: any) => {
                        const user = users.find(
                          (u: any) => u.id === assignment.user_id
                        );
                        return (
                          <View
                            key={assignment.id}
                            style={[
                              styles.assignmentRow,
                              {
                                backgroundColor: colors.card,
                                borderColor: colors.border,
                              },
                            ]}
                          >
                            <View style={styles.assignmentInfo}>
                              <Text
                                style={[
                                  styles.assignmentEmail,
                                  { color: colors.text },
                                ]}
                              >
                                {assignment.user_email ||
                                  user?.email ||
                                  `User ${assignment.user_id}`}
                              </Text>
                              <Text
                                style={[
                                  styles.assignmentName,
                                  { color: colors.secondary },
                                ]}
                              >
                                {assignment.user_name || user?.name || '—'}
                              </Text>
                              <Text
                                style={[
                                  styles.assignmentStatus,
                                  {
                                    color: assignment.is_enabled
                                      ? palette.brightGreen
                                      : palette.darkGrayBlue,
                                  },
                                ]}
                              >
                                {assignment.is_enabled ? 'Enabled' : 'Disabled'}
                              </Text>
                            </View>
                            {Platform.OS === 'web' ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveUser(assignment.user_id);
                                }}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                                title="Remove assignment"
                              >
                                <Ionicons
                                  name="trash"
                                  size={20}
                                  color="#ff4444"
                                />
                              </button>
                            ) : (
                              <TouchableOpacity
                                onPress={() =>
                                  handleRemoveUser(assignment.user_id)
                                }
                                style={styles.removeButton}
                              >
                                <Ionicons
                                  name="trash"
                                  size={20}
                                  color="#ff4444"
                                />
                              </TouchableOpacity>
                            )}
                          </View>
                        );
                      })
                    ) : (
                      <Text
                        style={[styles.emptyText, { color: colors.secondary }]}
                      >
                        No user assignments
                      </Text>
                    )}
                  </View>
                </>
              )}
            </ScrollView>
            <View style={styles.modalFooter}>
              <Button
                title="Close"
                onPress={() => setShowDetailsModal(false)}
                style={styles.modalButton}
              />
            </View>
          </View>
        </View>
      </Modal>

      {/* User Assignment Modal */}
      <Modal
        visible={showUserAssignmentModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowUserAssignmentModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View
            style={[
              styles.modalContent,
              { backgroundColor: colors.background },
            ]}
          >
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: colors.text }]}>
                Assign to User
              </Text>
              <TouchableOpacity
                onPress={() => setShowUserAssignmentModal(false)}
              >
                <Ionicons name="close" size={24} color={colors.text} />
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.modalBody}>
              <View style={styles.formGroup}>
                <Text style={[styles.label, { color: colors.text }]}>
                  Select User
                </Text>
                <Text style={[styles.helpText, { color: colors.secondary }]}>
                  Choose a specific user to assign this feature flag to. The
                  flag will be enabled for the selected user.
                </Text>
                <SearchableSelector
                  options={users.map((user: any) => ({
                    value: user.id,
                    label: user.email,
                    subtitle: user.name || undefined,
                  }))}
                  value={selectedUserId}
                  onChange={(value) =>
                    setSelectedUserId(value ? Number(value) : null)
                  }
                  placeholder="Select a user"
                  searchPlaceholder="Search users by email or name..."
                  emptyMessage="No users found"
                />
              </View>
            </ScrollView>
            <View style={styles.modalFooter}>
              <Button
                title="Cancel"
                onPress={() => setShowUserAssignmentModal(false)}
                variant="outline"
                style={styles.modalButton}
              />
              <Button
                title="Assign"
                onPress={handleAssignUser}
                loading={assignFlag.isPending}
                disabled={!selectedUserId}
                style={styles.modalButton}
              />
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  mobileContainer: {
    padding: 20,
    alignItems: 'center',
  },
  mobileTitle: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 8,
  },
  mobileSubtitle: {
    fontSize: 14,
    textAlign: 'center',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
  },
  createButton: {
    minWidth: 120,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    width: '100%',
    maxWidth: 600,
    maxHeight: '80%',
    borderRadius: 12,
    padding: 20,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
  },
  modalBody: {
    maxHeight: 400,
  },
  modalFooter: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 20,
  },
  modalButton: {
    minWidth: 100,
  },
  formGroup: {
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
  },
  textArea: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    minHeight: 80,
    textAlignVertical: 'top',
  },
  readOnlyInput: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  checkboxLabel: {
    marginLeft: 8,
    fontSize: 16,
  },
  detailRow: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  detailLabel: {
    fontSize: 14,
    fontWeight: '600',
    width: 120,
  },
  detailValue: {
    fontSize: 14,
    flex: 1,
  },
  detailSection: {
    marginTop: 20,
    paddingTop: 20,
    borderTopWidth: 1,
  },
  detailSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  detailSectionTitle: {
    fontSize: 16,
    fontWeight: '700',
  },
  smallButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  assignmentRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    marginBottom: 8,
  },
  assignmentInfo: {
    flex: 1,
  },
  assignmentEmail: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },
  assignmentName: {
    fontSize: 12,
    marginBottom: 4,
  },
  assignmentStatus: {
    fontSize: 12,
    fontWeight: '600',
  },
  removeButton: {
    padding: 8,
  },
  emptyText: {
    fontSize: 14,
    fontStyle: 'italic',
    textAlign: 'center',
    padding: 20,
  },
  helpText: {
    fontSize: 12,
    marginBottom: 8,
    lineHeight: 16,
  },
});

export default FeatureFlagsGrid;
